import React, { useCallback, useEffect, useState , useRef } from 'react';
import { Main, Bottom, Label, FormButton, Form, Field, Text } from './styles';
import Button from '../../button';
import { getCurrentProjectDetails } from '../../../services/projectServices';
import { getWorkspaceDetails } from '../../../services/workspaceService';
import { isEmpty, isEmail, getColorIndex } from '../../../helpers/common';
import { dispatchSettingError, dispatchSettingSuccess, inviteNewUser } from '../../../services/settingServices';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '../../../services/logService';
import { WORKSPACE_ANALYTICS } from '../../../global/analyticsConstants';
import { BUTTON_TYPE, ICON_COLORS } from '../../../global/constants';
import { DropdownItem } from '../../dropdown';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ProjectModel } from '../../../mobx/models/project';
import DropdownSearch from './dropdownSearch';
import ProjectIconAvatar from '../../projectIconAvatar';
import { useMobileDevice } from '../../../global/useMobile';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  onCancel: (value?: string) => void;
  project_id?: string;
  isTextVisible?: boolean;
  projectName?: string;
  workspace_id: string;
}

function InviteTeamsModal(props: Props) {
  const { onCancel, project_id, projectName, isTextVisible, workspace_id } = props;
  //States
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<ProjectModel[]>([]);
  const [projectSearch, setProjectSearch] = useState('');

  // Ref variables
  const emailsInputRef = useRef<HTMLInputElement>(null);

  //mobx store variable
  const { projectStore, workspaceStore } = rootStore;
  const { projects } = projectStore;
  const { currentWorkspace } = workspaceStore;

  //Other variable
  const currentProject = projects?.find((p) => p.id === project_id);
  const isMobile = useMobileDevice();

  useEffect(() => {
    if (project_id && currentProject) {
      setSelectedProjects([currentProject]);
    }
  }, [project_id, currentProject]);

  const onClickInvite = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        const invitePromises = emails.map((email) => {
          const payload = {
            companyId: workspace_id,
            email,
            projectIds: selectedProjects.map((project) => project.id)
          };
          return inviteNewUser(payload);
        });
        const result = await Promise.all(invitePromises);
        // inviteNewUser returns a falsy value and shows its own error toast when a call fails,
        // so only show the success toast if at least one invite actually went through
        const isInvited = result.some(Boolean);
        if (isInvited) {
          dispatchSettingSuccess('Users invited successfully!');
          trackAnalyticActivity(WORKSPACE_ANALYTICS.USER_INVITED);
          emails.length === 1 ? onCancel(result[0]?.redirectUri) : onCancel();
          await getWorkspaceDetails(workspace_id, { force: true });
          if (!isEmpty(project_id)) await getCurrentProjectDetails(project_id);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [emails, workspace_id, selectedProjects, onCancel, project_id]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const addEmailTag = useCallback(() => {
    const newEmails = inputValue
      .split(/[\s,]+/)
      .map((email) => email.trim())
      .filter((email) => email);

    if (newEmails.length) {
      // Validate emails
      const invalidEmails = newEmails.filter((email) => !isEmail(email));
      if (invalidEmails.length > 0) {
        dispatchSettingError('Please add proper email address');
        return;
      }
      const isEmailAdded = newEmails.filter((email) => emails.includes(email));
      if (isEmailAdded.length > 0) {
        rootStore.alertNotificationStore.setWarningMessage('This User is already added');
        setInputValue('');
        return;
      }
      const isExistingEmail = newEmails.some((email) =>
        [...(currentWorkspace?.users || []), ...(currentWorkspace?.invitedUsers || [])].some(
          (user) => user.email === email
        )
      );
      if (isExistingEmail) {
        rootStore.alertNotificationStore.setWarningMessage('User is already exists in this workspace');
        setInputValue('');
        return;
      }
      setEmails([...emails, ...newEmails]);
      setInputValue('');
    }
  }, [currentWorkspace?.invitedUsers, currentWorkspace?.users, emails, inputValue]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      setEmails(emails.slice(0, -1)); // Remove last email completely
    }

    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && inputValue.trim() !== '') {
      e.preventDefault();
      addEmailTag();
    }
  };

  // Handler to remove a selected project
  const handleRemove = (project: ProjectModel) => {
    setSelectedProjects((prev) => prev.filter((p) => p.id !== project.id));
  };

  const onEnterInProjects = useCallback(() => {
    const match = projects?.find((p) => p.name?.toLowerCase() === projectSearch.trim()?.toLowerCase());
    if (match && !selectedProjects.some((p) => p.id === match.id)) {
      setSelectedProjects([...selectedProjects, match]);
      setProjectSearch('');
    }
  }, [projectSearch, projects, selectedProjects]);

  return (
    <Main onSubmit={(e) => onClickInvite(e)}>
      <ModalHeader title='Invite teammates' onClose={() => onCancel()} />
      <Bottom>
        <Form>
          <Text isTextVisible={!isTextVisible}>Invite member to {projectName} project.</Text>
          <Input
            label='Email addresses'
            inputType='tags'
            minHeight='64px'
            onClick={() => emailsInputRef.current?.focus()}
            multiSelectProps={{
              value: inputValue,
              placeholderWhenEmpty: 'name@gmail.com, name@gmail.com,...',
              selectedUsers:
                emails?.map((email, index) => ({
                  id: index,
                  displayName: email
                })) || [],
              onBlur: addEmailTag,
              onChange: handleInputChange,
              inputRef: emailsInputRef,
              onKeyDown: handleInputKeyDown,
              onRemoveUser: () => {
                setEmails(emails.slice(0, -1));
              }
            }}
          />
          <Field>
            <Label>
              Add to projects <span>(optional)</span>
            </Label>
            <DropdownSearch
              modalTitle='Select Projects'
              onChangeSearch={isMobile ? setProjectSearch : undefined}
              isMinWidth={368}
              selectedOptions={selectedProjects}
              onRemove={handleRemove}
              searchValue={projectSearch}
              onSearchChange={setProjectSearch}
              onEnterOrComma={onEnterInProjects}
              placeholder='Select project'>
              {projects
                ?.filter((p) => p.name?.toLowerCase().includes(projectSearch?.toLowerCase()))
                ?.map((project) => {
                  const isChecked = selectedProjects.some((p) => p.id === project.id);
                  return (
                    <DropdownItem
                      isChecked={isChecked}
                      isActive={isChecked}
                      key={project.id}
                      label={project.name}
                      onClick={() => {
                        setSelectedProjects((prev) =>
                          prev.find((p) => p.id === project.id)
                            ? prev.filter((p) => p.id !== project.id)
                            : [...prev, project]
                        );
                        setProjectSearch('');
                      }}
                      customIcon={
                        <ProjectIconAvatar
                          type={project.icon?.type}
                          reference={project.icon?.reference || project?.name}
                          backgroundColor={
                            project.icon?.color
                              ? ICON_COLORS[project.icon?.color]
                              : ICON_COLORS[getColorIndex(project?.name)]
                          }
                          projectName={project.name}
                          size={isMobile ? 24 : 16}
                          iconSize={isMobile ? 16 : 9}
                          fontSize={isMobile ? '16px' : '9px'}
                          lineHeight={isMobile ? '24px' : '16px'}
                          borderRadius='4px'
                        />
                      }
                    />
                  );
                })}
            </DropdownSearch>
          </Field>
        </Form>
        <FormButton>
          <Button
            title='Send'
            variant={BUTTON_TYPE.PRIMARY}
            buttonType='button'
            isLoading={loading}
            disabled={loading || emails.length === 0}
            onClick={onClickInvite}
          />
        </FormButton>
      </Bottom>
    </Main>
  );
}

export default observer(InviteTeamsModal);
